// Normalización canónica de email del lado del cliente. **Espejo exacto de
// `libs/common/email.ts`** — si cambia una, cambia la otra.
//
// Está duplicado a propósito: cada SPA es un build de Vite independiente, sin
// acceso a `libs/`. La duplicación es de ~20 líneas y el test de abajo la
// mantiene honesta; compartir código implicaría acoplar los tres bundles.
//
// Regla del proyecto (decretada 2026-07-25): el email viaja SIEMPRE en
// minúsculas y sin espacios. Se fuerza en tres momentos distintos del ciclo de
// vida del input — tipeo, blur y submit — y otra vez en el Lambda. Las capas del
// cliente son por UX (el usuario VE lo que se va a mandar); la del Lambda es la
// que manda, porque el cliente es alterable.
//
// El porqué: el user pool de Cognito es case-sensitive de forma INMUTABLE,
// mientras que Aurora indexa por `lower(trim(email))`. Quien se registraba con
// mayúsculas no podía loguear nunca, pero SÍ recibía y completaba el "olvidé mi
// contraseña" — un loop infinito de resets exitosos que no desbloqueaban nada.
// ~3,1% del padrón de producción.

/**
 * Caracteres válidos en un email, ya en minúsculas: local-part dot-atom de RFC
 * 5322 (alfanuméricos, `.` y los specials `!#$%&'*+/=?^_\`{|}~-`), dominio
 * (alfanuméricos, `.`, `-`) y la `@`.
 *
 * No incluye espacios ni Unicode: no hacemos SMTPUTF8, y un homoglifo cirílico
 * en un dominio es phishing, no un tipeo.
 */
const INVALID_EMAIL_CHARS = /[^a-z0-9.!#$%&'*+/=?^_`{|}~@-]/g;

/** `Nombre Apellido <mail@x.com>`, el formato que produce copiar un contacto. */
const ANGLE_ADDR = /<([^<>]*)>/;

/**
 * Forma canónica de un email: trim + minúsculas + descarte de todo carácter
 * fuera del charset. Idempotente y total (nunca tira; devuelve `''` si la
 * entrada es basura).
 *
 * La extracción de la dirección entre ángulos evita que pegar
 * `Juan Perez <juan@gmail.com>` termine en `juanperezjuan@gmail.com`: un email
 * sintácticamente válido, pero de otra persona.
 */
export function normalizeEmail(raw: string): string {
  if (typeof raw !== 'string') return '';
  const angle = ANGLE_ADDR.exec(raw);
  return (angle?.[1] ?? raw).trim().toLowerCase().replace(INVALID_EMAIL_CHARS, '');
}

/** ¿Ya está en forma canónica? Para asserts en tests. */
export function isNormalizedEmail(raw: string): boolean {
  return raw === normalizeEmail(raw);
}

/**
 * Cuántos caracteres sobreviven a la normalización dentro de los primeros
 * `caret` del string. Sirve para NO patear el cursor al final cuando el filtro
 * borra algo que el usuario escribió en el medio del campo.
 */
export function caretAfterNormalize(raw: string, caret: number): number {
  return normalizeEmail(raw.slice(0, caret)).length;
}

/**
 * Atributos que apagan la "ayuda" del browser en un campo de email. Sin esto,
 * el teclado de iOS capitaliza la primera letra y el autocorrector reescribe el
 * dominio — que es, literalmente, cómo se generaron los ~8.000 registros con
 * mayúsculas del padrón.
 */
export const EMAIL_INPUT_ATTRS = {
  inputMode: 'email' as const,
  autoCapitalize: 'none',
  autoCorrect: 'off',
  spellCheck: false,
};

/**
 * Capas 1 y 2 listas para pegar en un `<input type="email">` suelto: filtrado
 * en vivo mientras se tipea/pega y re-forzado al perder el foco.
 *
 * En el login-portal no hace falta usarla — `FormField` ya aplica los guards a
 * todo `type="email"`. Es para los portales que usan `<input>` pelado.
 *
 * El manejo del caret evita que corregir una letra en el medio de un email
 * largo patee el cursor al final en cada tecla.
 */
export function emailInputProps(onValue: (value: string) => void): {
  inputMode: 'email';
  autoCapitalize: string;
  autoCorrect: string;
  spellCheck: boolean;
  onChange: (e: { currentTarget: HTMLInputElement }) => void;
  onBlur: (e: { currentTarget: HTMLInputElement }) => void;
} {
  return {
    ...EMAIL_INPUT_ATTRS,
    onChange: (e) => {
      const el = e.currentTarget;
      const raw = el.value;
      const clean = normalizeEmail(raw);
      if (clean !== raw) {
        const caret = el.selectionStart ?? raw.length;
        const next = caretAfterNormalize(raw, caret);
        el.value = clean;
        el.setSelectionRange(next, next);
      }
      onValue(clean);
    },
    onBlur: (e) => {
      onValue(normalizeEmail(e.currentTarget.value));
    },
  };
}
